import styled, { keyframes } from 'styled-components';

type SpinnerSize = 'sm' | 'md' | 'lg';

interface SpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Additional class name */
  className?: string;
}

const SIZE_MAP: Record<SpinnerSize, number> = {
  sm: 16,
  md: 24,
  lg: 32,
};

export default function Spinner({ size = 'md', className }: SpinnerProps) {
  const px = SIZE_MAP[size];
  return (
    <SpinnerSvg
      className={className}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <SpinnerCircle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </SpinnerSvg>
  );
}

/** Full-page or section loading state with optional message */
interface LoadingStateProps {
  message?: string;
  size?: SpinnerSize;
}

export function LoadingState({ message, size = 'md' }: LoadingStateProps) {
  return (
    <LoadingContainer>
      <Spinner size={size} />
      {message && <LoadingMessage>{message}</LoadingMessage>}
    </LoadingContainer>
  );
}

/**
 * `LoadingState` laid over a region whose content stays visible for context —
 * a player's poster while the video loads. Needs a `position: relative`
 * parent; the scrim takes that parent's corners (`border-radius: inherit`).
 * When the region has nothing worth showing yet, render `LoadingState` in its
 * place instead.
 */
export function LoadingOverlay({ message, size = 'lg' }: LoadingStateProps) {
  return (
    <OverlayScrim role="status" aria-live="polite">
      <LoadingState message={message} size={size} />
    </OverlayScrim>
  );
}

/* ─── Animations ─── */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* ─── Styled Components ─── */

const SpinnerSvg = styled.svg`
  animation: ${spin} 1s linear infinite;
  color: var(--primary);
`;

const SpinnerCircle = styled.circle`
  stroke-dasharray: 56;
  stroke-dashoffset: 20;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
`;

const OverlayScrim = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background-color: color-mix(in srgb, var(--background) 80%, transparent);
`;

const LoadingMessage = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
`;
